<script>
import VueApexCharts from 'vue-apexcharts'

import { apiSecond } from '@/boot/axios'

// Дашборд и журнал «Астана-1» ходят с одинаковыми фильтрами: имена параметров
// у GET /astana-ones/dashboards совпадают с GET /astana-ones/all.
const FILTER_QUERY_KEYS = [
	'type_operation',
	'iin_bin',
	'item_name',
	'item_code',
	'start_at',
	'finish_at',
]

// Переключатель в шапке — это фильтр type_operation (хранимые значения ИМ/ЭК).
const OPERATION_BY_MODE = { all: null, import: 'ИМ', export: 'ЭК' }
const MODE_BY_OPERATION = { ИМ: 'import', ЭК: 'export' }

const IMPORT_COLOR = '#157FEE'
const EXPORT_COLOR = '#E0BB49'
const STATE_COLOR = '#00E096'

// Палитра доната — цвета сайта, при нехватке идут по кругу.
const GROUP_COLORS = [
	'#157FEE',
	'#E0BB49',
	'#00E096',
	'#EB6834',
	'#9B8AFB',
	'#52A5FC',
	'#F64A4A',
	'#475F84',
	'#00B8D9',
	'#FF8A65',
]

// Бэк отдаёт все строки разрезов (страны — десятки, посты — тоже), срез топа — задача фронта:
// иначе карточка растёт на весь экран, а подписи на оси становятся нечитаемыми.
const TOP_COUNTRIES_LIMIT = 8
const TOP_STATES_LIMIT = 10
const CHART_ROW_HEIGHT = 38

const CHART_FONT = 'Geologica, sans-serif'
const AXIS_LABEL_STYLE = { colors: '#425166', fontSize: '12px', fontFamily: CHART_FONT }
const GRID_COLOR = '#EEF1F6'

// Вес по всей таблице — это десятки миллионов килограммов, полные числа на шкале
// наезжают друг на друга. Поэтому шкала — короткими подписями и с малым числом делений.
const AXIS_TICKS = 4

// Страны лежат в базе официальными названиями («Королевство Норвегия») — на оси
// они не помещаются. Словарь нужен там, где короткое имя не получается простым
// отбрасыванием слов: «Турецкая Республика» → «Турция».
const COUNTRY_SHORT = {
	'китайская народная республика': 'Китай',
	'российская федерация': 'Россия',
	'кыргызская республика': 'Кыргызстан',
	'турецкая республика': 'Турция',
	'соединенные штаты америки': 'США',
	'соединённые штаты америки': 'США',
	'соединенное королевство великобритании и северной ирландии': 'Великобритания',
	'соединённое королевство великобритании и северной ирландии': 'Великобритания',
	'латвийская республика': 'Латвия',
	'литовская республика': 'Литва',
	'эстонская республика': 'Эстония',
	'азербайджанская республика': 'Азербайджан',
	'аргентинская республика': 'Аргентина',
	'французская республика': 'Франция',
	'итальянская республика': 'Италия',
	'португальская республика': 'Португалия',
	'чешская республика': 'Чехия',
	'словацкая республика': 'Словакия',
	'корейская народно-демократическая республика': 'КНДР',
	'республика корея': 'Корея',
	'королевство нидерландов': 'Нидерланды',
	'королевство таиланд': 'Таиланд',
	'королевство марокко': 'Марокко',
}

// Слова, которые ничего не добавляют к названию страны.
const COUNTRY_NOISE =
	/(^|\s)(республика|республики|королевство|королевства|федеративная|федерация|народная|демократическая|социалистическая|исламская|арабская|княжество|государство|содружество|союз)(?=\s|$)/gi

function toNumber(value) {
	const num = Number(value)
	return Number.isFinite(num) ? num : 0
}

function escapeHtml(value) {
	return String(value === null || value === undefined ? '' : value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
}

function shortCountry(name) {
	const raw = String(name || '').trim()
	if (!raw) return ''

	const known = COUNTRY_SHORT[raw.toLowerCase()]
	if (known) return known

	const stripped = raw.replace(COUNTRY_NOISE, ' ').replace(/\s+/g, ' ').trim()
	return stripped || raw
}

// Названия постов в базе длинные и в разных форматах: «ТАМОЖЕННЫЙ ПОСТ «МОРПОРТ»»,
// «ОРАЛ – ЦЕНТР ТАМОЖЕННОГО ОФОРМЛЕНИЯ», «ШЫМКЕНТ-ЦТО». Выбрасываем только
// канцелярскую часть — иначе подписи на оси обрезаются многоточием.
// Аббревиатуру «-ЦТО» оставляем: без неё разные посты сливаются в одну подпись.
const STATE_NOISE = /(таможенный пост|центр таможенного оформления)/i
const STATE_NOISE_PREFIX = /^(таможенный пост|центр таможенного оформления)\s*[-–—:]?\s*/i
const STATE_NOISE_SUFFIX = /\s*[-–—,]?\s*(таможенный пост|центр таможенного оформления)\s*$/i

function shortState(name) {
	const raw = String(name || '').trim()
	if (!raw) return ''

	// имя в кавычках — самая короткая и понятная подпись
	const quoted = raw.match(/[«"]([^»"]+)[»"]/)
	if (quoted) return quoted[1].trim()

	if (!STATE_NOISE.test(raw)) return raw

	const stripped = raw.replace(STATE_NOISE_PREFIX, '').replace(STATE_NOISE_SUFFIX, '').trim()
	return stripped || raw
}

function groupColor(index) {
	return GROUP_COLORS[index % GROUP_COLORS.length]
}

function niceCeil(value) {
	if (!(value > 0)) return 0

	const pow = Math.pow(10, Math.floor(Math.log10(value)))
	const norm = value / pow
	const step = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((item) => norm <= item) || 10

	return step * pow
}

// Верх шкалы = «круглый» шаг × число делений. Если этого не делать, apexcharts
// делит максимум на равные части и подписи получаются вида 2 455 287.
// headroom — запас справа: у графика «Импорт vs экспорт» в нём лежит плашка со значением.
function niceAxisMax(max, headroom) {
	if (!(max > 0)) return 0
	return niceCeil((max * headroom) / AXIS_TICKS) * AXIS_TICKS
}

export default {
	name: 'CustomsAnalytics',
	components: { VueApexCharts },
	data() {
		return {
			data: null,
			loading: false,
			mode: 'all', // all | import | export
			// Те же фильтры, что принимает дашборд; вид операции задаёт переключатель в шапке.
			filters: {
				iin_bin: null,
				item_name: null,
				item_code: null,
				start_at: null,
				finish_at: null,
			},
			errorMessage: {
				status: null,
				text: null,
			},
		}
	},
	computed: {
		stats() {
			return this.data || {}
		},
		groupStats() {
			return Array.isArray(this.stats.item_group_stats) ? this.stats.item_group_stats : []
		},
		allCountryStats() {
			return Array.isArray(this.stats.country_stats) ? this.stats.country_stats : []
		},
		countryStats() {
			return this.allCountryStats.slice(0, TOP_COUNTRIES_LIMIT)
		},
		allStateStats() {
			return Array.isArray(this.stats.state_stats) ? this.stats.state_stats : []
		},
		stateStats() {
			return this.allStateStats.slice(0, TOP_STATES_LIMIT)
		},
		declarationCount() {
			return toNumber(this.stats.declaration_count)
		},
		itemCount() {
			return toNumber(this.stats.item_count)
		},
		unclassifiedCount() {
			return toNumber(this.stats.unclassified_item_count)
		},
		totalWeight() {
			return toNumber(this.stats.total_weight)
		},
		totalWeightTon() {
			return toNumber(this.stats.total_weight_ton)
		},
		importWeight() {
			return toNumber(this.stats.weight_by_import)
		},
		exportWeight() {
			return toNumber(this.stats.weight_by_export)
		},
		hasImportExport() {
			return this.importWeight > 0 || this.exportWeight > 0
		},
		hasGroups() {
			return this.groupStats.some((group) => toNumber(group.weight) > 0)
		},
		hasCountries() {
			return this.countryStats.some((row) => toNumber(row.weight) > 0)
		},
		hasStates() {
			return this.stateStats.some((row) => toNumber(row.weight) > 0)
		},
		// Период не задан — бэк подставляет текущий год, об этом стоит предупредить.
		defaultPeriod() {
			return !this.filters.start_at && !this.filters.finish_at
		},

		/* ---------- Импорт vs экспорт ---------- */
		importExportSeries() {
			return [
				{
					name: this.$t('customs.weight_kg'),
					data: [this.importWeight, this.exportWeight],
				},
			]
		},
		importExportChartOptions() {
			const $this = this
			const max = Math.max(this.importWeight, this.exportWeight)
			const axisMax = niceAxisMax(max, 1.15)

			return {
				chart: {
					type: 'bar',
					fontFamily: CHART_FONT,
					toolbar: { show: false },
				},
				plotOptions: {
					bar: {
						horizontal: true,
						distributed: true,
						borderRadius: 4,
						barHeight: '38%',
						dataLabels: { position: 'top' },
					},
				},
				colors: [IMPORT_COLOR, EXPORT_COLOR],
				dataLabels: {
					enabled: true,
					// apexcharts центрирует подпись по концу бара, поэтому она лежит
					// и на заливке, и на фоне карточки — читаемой её делает белая плашка.
					offsetX: 18,
					// у бара подпись короткая, точное значение — в подсказке
					formatter: (val) => $this.formatCompact(val),
					style: {
						fontSize: '12px',
						fontFamily: CHART_FONT,
						fontWeight: 500,
						colors: ['#151D48'],
					},
					background: {
						enabled: true,
						foreColor: '#FFFFFF',
						padding: 5,
						borderRadius: 4,
						borderWidth: 1,
						borderColor: GRID_COLOR,
						opacity: 0.95,
						dropShadow: { enabled: false },
					},
				},
				legend: { show: false },
				grid: {
					borderColor: GRID_COLOR,
					xaxis: { lines: { show: true } },
					yaxis: { lines: { show: false } },
				},
				xaxis: {
					// запас справа, чтобы подпись значения не упиралась в край
					max: axisMax || undefined,
					tickAmount: AXIS_TICKS,
					categories: [this.$t('customs.import'), this.$t('customs.export')],
					labels: { style: AXIS_LABEL_STYLE, formatter: (val) => $this.formatCompact(val) },
					axisBorder: { show: false },
					axisTicks: { show: false },
				},
				yaxis: { labels: { style: AXIS_LABEL_STYLE } },
				tooltip: {
					custom: ({ dataPointIndex }) => $this.importExportTooltip(dataPointIndex),
				},
			}
		},

		/* ---------- Товарные группы ---------- */
		groupSeries() {
			return this.groupStats.map((group) => toNumber(group.weight))
		},
		groupChartOptions() {
			const $this = this

			return {
				chart: {
					type: 'donut',
					fontFamily: CHART_FONT,
					toolbar: { show: false },
					events: {
						// клик по сектору — тот же drill-down, что и по чипсу под диаграммой
						dataPointSelection(event, chartContext, config) {
							$this.toggleGroup($this.groupStats[config.dataPointIndex])
						},
					},
				},
				labels: this.groupStats.map((group) => group.title || this.$t('customs.no_data')),
				colors: this.groupStats.map((group, index) => groupColor(index)),
				legend: { show: false },
				stroke: { width: 2, colors: ['#FFFFFF'] },
				dataLabels: {
					enabled: true,
					// мелкие доли не подписываем — цифры сливаются
					formatter: (percent) => (percent >= 7 ? Math.round(percent) + '%' : ''),
					style: { fontSize: '12px', fontFamily: CHART_FONT, fontWeight: 500 },
					// белая подпись поверх светлых секторов (золотой, зелёный) без тени не читается
					dropShadow: { enabled: true, top: 0, left: 0, blur: 2, opacity: 0.45 },
				},
				plotOptions: {
					pie: {
						expandOnClick: false,
						donut: {
							size: '68%',
							labels: {
								show: true,
								name: {
									fontFamily: CHART_FONT,
									fontSize: '13px',
									color: '#425166',
								},
								value: {
									fontFamily: CHART_FONT,
									fontSize: '18px',
									fontWeight: 600,
									color: '#151D48',
									formatter: (val) => $this.formatWeight(val),
								},
								total: {
									show: true,
									showAlways: true,
									label: $this.$t('customs.weight_kg'),
									color: '#425166',
									fontFamily: CHART_FONT,
									fontSize: '13px',
									formatter: () =>
										$this.formatWeight($this.groupSeries.reduce((sum, val) => sum + val, 0)),
								},
							},
						},
					},
				},
				states: { active: { filter: { type: 'none' } } },
				// у доната индекс сектора приходит как seriesIndex (в барах — dataPointIndex)
				tooltip: { custom: ({ seriesIndex }) => $this.groupTooltip(seriesIndex) },
			}
		},

		/* ---------- Топ стран-партнёров ---------- */
		countrySeries() {
			return [
				{
					name: this.$t('customs.weight_kg'),
					data: this.countryStats.map((row) => toNumber(row.weight)),
				},
			]
		},
		countryChartOptions() {
			const $this = this

			return {
				chart: {
					type: 'bar',
					fontFamily: CHART_FONT,
					toolbar: { show: false },
				},
				plotOptions: {
					bar: { horizontal: true, distributed: true, borderRadius: 4, barHeight: '62%' },
				},
				// цвет бара задаёт направление операции
				colors: this.countryStats.map((row) =>
					String(row.type_operation || '').trim() === 'ЭК' ? EXPORT_COLOR : IMPORT_COLOR,
				),
				dataLabels: { enabled: false },
				legend: { show: false },
				grid: {
					borderColor: GRID_COLOR,
					xaxis: { lines: { show: true } },
					yaxis: { lines: { show: false } },
				},
				xaxis: {
					max: niceAxisMax(Math.max.apply(null, [0].concat(this.countrySeries[0].data)), 1.02),
					tickAmount: AXIS_TICKS,
					categories: this.countryStats.map((row) => shortCountry(row.country)),
					labels: { style: AXIS_LABEL_STYLE, formatter: (val) => $this.formatCompact(val) },
					axisBorder: { show: false },
					axisTicks: { show: false },
				},
				yaxis: { labels: { style: AXIS_LABEL_STYLE, maxWidth: 150 } },
				tooltip: { custom: ({ dataPointIndex }) => $this.countryTooltip(dataPointIndex) },
			}
		},
		countryChartHeight() {
			return Math.max(240, this.countryStats.length * CHART_ROW_HEIGHT + 60)
		},

		/* ---------- Нагрузка по таможенным постам ---------- */
		stateSeries() {
			return [
				{
					name: this.$t('customs.weight_kg'),
					data: this.stateStats.map((row) => toNumber(row.weight)),
				},
			]
		},
		stateChartOptions() {
			const $this = this

			return {
				chart: {
					type: 'bar',
					fontFamily: CHART_FONT,
					toolbar: { show: false },
				},
				plotOptions: {
					bar: { horizontal: true, borderRadius: 4, barHeight: '62%' },
				},
				colors: [STATE_COLOR],
				dataLabels: { enabled: false },
				legend: { show: false },
				grid: {
					borderColor: GRID_COLOR,
					xaxis: { lines: { show: true } },
					yaxis: { lines: { show: false } },
				},
				xaxis: {
					max: niceAxisMax(Math.max.apply(null, [0].concat(this.stateSeries[0].data)), 1.02),
					tickAmount: AXIS_TICKS,
					categories: this.stateStats.map(
						(row) => shortState(row.state) || this.$t('customs.state_empty'),
					),
					labels: { style: AXIS_LABEL_STYLE, formatter: (val) => $this.formatCompact(val) },
					axisBorder: { show: false },
					axisTicks: { show: false },
				},
				yaxis: { labels: { style: AXIS_LABEL_STYLE, maxWidth: 170 } },
				tooltip: { custom: ({ dataPointIndex }) => $this.stateTooltip(dataPointIndex) },
			}
		},
		stateChartHeight() {
			return Math.max(240, this.stateStats.length * CHART_ROW_HEIGHT + 60)
		},
	},
	methods: {
		groupColor(index) {
			return groupColor(index)
		},
		formatNumber(value) {
			const num = toNumber(value)
			return this.$options.filters.formatNumber(num) || '0'
		},
		formatWeight(value) {
			return this.formatNumber(Math.round(toNumber(value)))
		},
		formatTon(value) {
			return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(toNumber(value))
		},
		// Короткая запись для шкал и подписей баров: 20 460 725 → «20,5 млн».
		// Точное значение всегда есть в подсказке и в карточке итогов.
		formatCompact(value) {
			const num = toNumber(value)
			const abs = Math.abs(num)

			// границы чуть ниже круглых, иначе 999 999 округляется в «1 000 тыс.»
			if (abs >= 999.5e6)
				return this.compactValue(num / 1e9) + ' ' + this.$t('customs.unit_billion')
			if (abs >= 999.5e3)
				return this.compactValue(num / 1e6) + ' ' + this.$t('customs.unit_million')
			if (abs >= 1e4) return this.compactValue(num / 1e3) + ' ' + this.$t('customs.unit_thousand')

			return this.formatWeight(num)
		},
		compactValue(value) {
			return new Intl.NumberFormat('ru-RU', {
				maximumFractionDigits: Math.abs(value) < 100 ? 1 : 0,
			}).format(value)
		},
		operationLabel(type) {
			const value = String(type || '').trim()
			if (value === 'ИМ') return this.$t('customs.import')
			if (value === 'ЭК') return this.$t('customs.export')
			return value || '—'
		},

		/* ---------- Подсказки графиков ---------- */
		// Подписи на осях сокращены, поэтому в подсказке — полное название и все метрики строки.
		chartTooltip(title, rows) {
			const body = rows
				.filter((row) => row.value !== null && row.value !== undefined && row.value !== '')
				.map(
					(row) =>
						'<div class="ca-tip__row"><span>' +
						escapeHtml(row.label) +
						'</span><b>' +
						escapeHtml(row.value) +
						'</b></div>',
				)
				.join('')

			return (
				'<div class="ca-tip"><div class="ca-tip__title">' +
				escapeHtml(title) +
				'</div>' +
				body +
				'</div>'
			)
		},
		importExportTooltip(index) {
			const isImport = index === 0

			return this.chartTooltip(isImport ? this.$t('customs.import') : this.$t('customs.export'), [
				{
					label: this.$t('customs.weight_kg'),
					value: this.formatWeight(isImport ? this.importWeight : this.exportWeight),
				},
				{
					label: this.$t('customs.price_total'),
					value: this.formatWeight(
						isImport ? this.stats.price_by_import : this.stats.price_by_export,
					),
				},
			])
		},
		groupTooltip(index) {
			const row = this.groupStats[index]
			if (!row) return ''

			return this.chartTooltip(row.title || this.$t('customs.no_data'), [
				{ label: this.$t('customs.item_code_placeholder'), value: row.code || '—' },
				{ label: this.$t('customs.weight_kg'), value: this.formatWeight(row.weight) },
				{ label: this.$t('customs.price_total'), value: this.formatWeight(row.price) },
				{
					label: this.$t('customs.declarations_short'),
					value: this.formatNumber(row.declaration_count),
				},
				{ label: this.$t('customs.items_short'), value: this.formatNumber(row.item_count) },
			])
		},
		countryTooltip(index) {
			const row = this.countryStats[index]
			if (!row) return ''

			return this.chartTooltip(row.country || this.$t('customs.no_data'), [
				{ label: this.$t('customs.direction'), value: this.operationLabel(row.type_operation) },
				{ label: this.$t('customs.weight_kg'), value: this.formatWeight(row.weight) },
				{ label: this.$t('customs.price_total'), value: this.formatWeight(row.price) },
				{
					label: this.$t('customs.declarations_short'),
					value: this.formatNumber(row.declaration_count),
				},
				{ label: this.$t('customs.items_short'), value: this.formatNumber(row.item_count) },
			])
		},
		stateTooltip(index) {
			const row = this.stateStats[index]
			if (!row) return ''

			return this.chartTooltip(row.state || this.$t('customs.state_empty'), [
				{ label: this.$t('customs.weight_kg'), value: this.formatWeight(row.weight) },
				{ label: this.$t('customs.price_total'), value: this.formatWeight(row.price) },
				{
					label: this.$t('customs.declarations_short'),
					value: this.formatNumber(row.declaration_count),
				},
				{ label: this.$t('customs.items_short'), value: this.formatNumber(row.item_count) },
			])
		},

		/* ---------- Фильтры ---------- */
		// Вид операции переключается кнопками в шапке — как на макете.
		setMode(mode) {
			if (this.mode === mode) return
			this.mode = mode
			this.applyFilters()
		},
		isActiveGroup(group) {
			if (!group || !group.code) return false
			return String(this.filters.item_code || '') === String(group.code)
		},
		// Drill-down по товарной группе: чипс (и сектор доната) кладёт код группы
		// в item_code, повторный клик по активной группе его снимает.
		toggleGroup(group) {
			if (!group || !group.code) return

			this.filters.item_code = this.isActiveGroup(group) ? null : String(group.code)
			this.applyFilters()
		},
		chipTitle(group) {
			if (!group) return ''
			return group.title + ' · ' + this.formatWeight(group.weight) + ' ' + this.$t('customs.kg')
		},
		search() {
			// item_code бэк принимает только цифрами, иначе 422
			if (this.filters.item_code) {
				this.filters.item_code = String(this.filters.item_code).replace(/\D/g, '') || null
			}
			this.applyFilters()
		},
		clearFilter() {
			this.filters = {
				iin_bin: null,
				item_name: null,
				item_code: null,
				start_at: null,
				finish_at: null,
			}
			this.mode = 'all'
			this.applyFilters()
		},
		clearItemCode() {
			this.filters.item_code = null
			this.applyFilters()
		},
		applyFilters() {
			this.pushQuery()
			this.fetchDashboard()
		},
		// Фильтры в виде, который принимает API: плоские параметры с теми же именами,
		// что у GET /astana-ones/all — так один и тот же набор сужает и таблицу, и дашборд.
		activeFilters() {
			const active = {}
			const operation = OPERATION_BY_MODE[this.mode]

			if (operation) active.type_operation = operation
			if (this.filters.iin_bin) active.iin_bin = String(this.filters.iin_bin).trim()
			if (this.filters.item_name) active.item_name = String(this.filters.item_name).trim()
			if (this.filters.item_code) active.item_code = String(this.filters.item_code).trim()
			if (this.filters.start_at) active.start_at = this.filters.start_at
			if (this.filters.finish_at) active.finish_at = this.filters.finish_at

			return active
		},
		// Фильтры зеркалим в query — ссылкой на дашборд можно поделиться.
		pushQuery() {
			const query = { ...this.$route.query }
			FILTER_QUERY_KEYS.forEach((key) => delete query[key])

			const active = this.activeFilters()
			Object.keys(active).forEach((key) => {
				query[key] = active[key]
			})

			this.$router.replace({ query }).catch(() => {})
		},
		syncFiltersFromRoute() {
			const query = this.$route.query

			if (query.type_operation) this.mode = MODE_BY_OPERATION[query.type_operation] || 'all'
			if (query.iin_bin) this.filters.iin_bin = query.iin_bin
			if (query.item_name) this.filters.item_name = query.item_name
			if (query.item_code) this.filters.item_code = query.item_code
			if (query.start_at) this.filters.start_at = query.start_at
			if (query.finish_at) this.filters.finish_at = query.finish_at
		},

		/* ---------- Данные ---------- */
		async fetchDashboard() {
			this.loading = true
			this.errorMessage = { status: null, text: null }

			try {
				const response = await apiSecond.get('v1/astana-ones/dashboards', {
					params: this.activeFilters(),
				})

				// Ответ дашборда — плоский объект, без обёртки data
				this.data = response.data || {}
			} catch (error) {
				this.catchError(error)
				// На первом запросе ошибку показывает preloader, дальше данные уже на экране — тост.
				if (this.data && this.errorMessage.text) {
					this.showToast(this.errorMessage.text, 'error')
				}
			} finally {
				this.loading = false
			}
		},
		showToast(text, color = 'success') {
			this.$toast[color](text, { timeout: 5000 })
		},
		catchError(error) {
			if (error?.response?.status == 500) {
				this.errorMessage.status = 500
				this.errorMessage.text = this.$t('system_message.500')
			}
			if (error?.response?.status == 401) {
				this.errorMessage.status = 401
				this.errorMessage.text = error.response.data.error_message
				this.$router.push('/' + this.$i18n.locale + '/login')
			}
			if (error?.response?.status == 422) {
				// Невалидные фильтры бэк отдаёт как error_messages: {поле: [текст]}
				this.errorMessage.status = 422
				this.errorMessage.text =
					this.firstFieldMessage(error.response.data?.error_messages) ||
					error.response.data?.error_message
			}
			if (error?.response?.status == 403) {
				this.errorMessage.status = 403
				this.errorMessage.text = error?.response?.data?.message
			}
		},
		firstFieldMessage(errorMessages) {
			if (!errorMessages || typeof errorMessages !== 'object') return null
			return Object.values(errorMessages).flat().filter(Boolean)[0] || null
		},
	},
	beforeCreate() {
		if (!localStorage.token) this.$router.push('/ru/login')
	},
	mounted() {
		this.syncFiltersFromRoute()
		this.fetchDashboard()
	},
	head: {
		title() {
			return {
				inner: this.$t('sidebar.customs_analytics_page'),
			}
		},
		meta: [],
	},
}
</script>

<template>
	<div class="wrap__content customs-analytics" v-bind:class="{ 'wrap__content--preloader': !data }">
		<v-preloader v-if="!data" :message="errorMessage"></v-preloader>

		<div class="container" v-if="data">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="['CustomsAnalytics']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content">
							<div class="card__content-header">
								<div class="content__title w-full">
									<div
										class="content__title--element flex justify-between items-center gap-4 flex-wrap w-full"
									>
										<div class="content__title--text">
											{{ $t('sidebar.customs_analytics_page') }}
										</div>
										<div class="inline-flex my-2">
											<button
												class="text-[#52A5FC] text-[16px] font-medium leading-none py-[12px] px-[16px] bg-[#FBFBFB] hover:no-underline hover:bg-[#53A6FC] hover:text-[#FFFFFF] duration-300 first:rounded-tl-[30px] first:rounded-bl-[30px] last:rounded-tr-[30px] last:rounded-br-[30px]"
												:class="{
													'bg-[#53A6FC] text-[#FFFFFF]': mode === 'all',
												}"
												@click="setMode('all')"
											>
												{{ $t(`labels.all_operations`) }}
											</button>
											<button
												class="text-[#52A5FC] text-[16px] font-medium leading-none py-[12px] px-[16px] bg-[#FBFBFB] hover:no-underline hover:bg-[#53A6FC] hover:text-[#FFFFFF] duration-300 first:rounded-tl-[30px] first:rounded-bl-[30px] last:rounded-tr-[30px] last:rounded-br-[30px]"
												:class="{
													'bg-[#53A6FC] text-[#FFFFFF]': mode === 'import',
												}"
												@click="setMode('import')"
											>
												{{ $t(`labels.import`) }}
											</button>
											<button
												class="text-[#52A5FC] text-[16px] font-medium leading-none py-[12px] px-[16px] bg-[#FBFBFB] hover:no-underline hover:bg-[#53A6FC] hover:text-[#FFFFFF] duration-300 first:rounded-tl-[30px] first:rounded-bl-[30px] last:rounded-tr-[30px] last:rounded-br-[30px]"
												:class="{
													'bg-[#53A6FC] text-[#FFFFFF]': mode === 'export',
												}"
												@click="setMode('export')"
											>
												{{ $t(`labels.export`) }}
											</button>
										</div>
									</div>
								</div>
							</div>
							<div class="card__content--body">
								<div class="table__block--content">
									<div class="table__block--filter">
										<div class="w-full row gap-y-2">
											<div class="col-12 col-md-6 col-lg-4">
												<label class="search__label">
													<input
														v-model="filters.iin_bin"
														type="text"
														name="filter_iin_bin"
														:placeholder="$t('headers.iin_bin')"
														@keyup.enter="search"
													/>
												</label>
											</div>

											<div class="col-12 col-md-6 col-lg-4">
												<label class="search__label">
													<input
														v-model="filters.item_name"
														type="text"
														name="filter_item_name"
														:placeholder="$t('headers.item_name')"
														@keyup.enter="search"
													/>
												</label>
											</div>

											<div class="col-12 col-md-6 col-lg-4">
												<label class="search__label">
													<input
														v-model="filters.item_code"
														type="text"
														inputmode="numeric"
														name="filter_item_code"
														:placeholder="$t('customs.item_code_placeholder')"
														@keyup.enter="search"
													/>
												</label>
											</div>

											<div class="col-12 col-md-6 col-lg-3">
												<div class="monitoring__line--date">
													<div class="monitoring__date--item w-full">
														<div class="monitoring__date--text">{{ $t('filters.after') }}</div>
														<div class="monitoring__date--input w-full">
															<input
																v-model="filters.start_at"
																type="date"
																class="date__input w-full"
															/>
														</div>
													</div>
												</div>
											</div>

											<div class="col-12 col-md-6 col-lg-3">
												<div class="monitoring__line--date">
													<div class="monitoring__date--item w-full">
														<div class="monitoring__date--text">{{ $t('filters.before') }}</div>
														<div class="monitoring__date--input w-full">
															<input
																v-model="filters.finish_at"
																type="date"
																class="date__input w-full"
															/>
														</div>
													</div>
												</div>
											</div>

											<div class="col-12 col-lg-auto flex flex-wrap items-end gap-2">
												<button class="input-btn form__line--btn" @click="search">
													{{ $t('button.find') }}
												</button>
												<button class="btn--link btn--link--plus" @click.prevent.stop="clearFilter">
													{{ $t('button.clear') }}
												</button>
											</div>

											<!-- Подсказка и активный drill-down живут внутри .row: сам
											     .table__block--filter — это flex со space-between, и вторым
											     блоком текст уезжал вправо от фильтров. -->
											<div class="col-12 flex flex-wrap items-center gap-x-4 gap-y-2">
												<div v-if="defaultPeriod" class="ca-note">
													{{ $t('customs.default_period_hint') }}
												</div>
												<button
													v-if="filters.item_code"
													type="button"
													class="ca-pill"
													@click.prevent.stop="clearItemCode"
												>
													{{ $t('customs.item_code_placeholder') }}: {{ filters.item_code }}
													<span class="ca-pill__close">&times;</span>
												</button>
											</div>
										</div>
									</div>

									<div class="ca-board" :class="{ 'ca-board--loading': loading }">
										<div class="grid grid-cols-1 md:grid-cols-2 gap-[20px]">
											<div class="ca-card">
												<div class="ca-card__label">{{ $t('customs.declaration_count') }}</div>
												<div class="ca-card__value">{{ formatNumber(declarationCount) }}</div>
												<div class="ca-card__hint">
													{{ formatNumber(itemCount) }} {{ $t('customs.items_in_selection') }}
												</div>
											</div>

											<div class="ca-card">
												<div class="ca-card__label">{{ $t('customs.total_weight_kg') }}</div>
												<div class="ca-card__value">{{ formatWeight(totalWeight) }}</div>
												<div class="ca-card__hint">
													{{ formatTon(totalWeightTon) }} {{ $t('customs.tons') }}
												</div>
											</div>
										</div>

										<div class="grid grid-cols-1 xl:grid-cols-2 gap-[20px] mt-[20px]">
											<div class="ca-card">
												<div class="ca-card__title">{{ $t('customs.import_vs_export') }}</div>
												<div class="ca-card__hint mb-[10px]">
													{{ $t('customs.import_vs_export_hint') }}
												</div>

												<VueApexCharts
													v-if="hasImportExport"
													type="bar"
													:height="320"
													:options="importExportChartOptions"
													:series="importExportSeries"
												></VueApexCharts>
												<div v-else class="ca-empty">{{ $t('customs.no_data') }}</div>

												<div v-if="unclassifiedCount" class="ca-note mt-[10px]">
													{{
														$t('customs.unclassified_hint', {
															count: formatNumber(unclassifiedCount),
														})
													}}
												</div>
											</div>

											<div class="ca-card">
												<div class="ca-card__title">{{ $t('customs.item_groups') }}</div>
												<div class="ca-card__hint mb-[10px]">
													{{ $t('customs.item_groups_hint') }}
												</div>

												<template v-if="hasGroups">
													<VueApexCharts
														type="donut"
														:height="260"
														:options="groupChartOptions"
														:series="groupSeries"
													></VueApexCharts>

													<div class="ca-chips">
														<button
															v-for="(group, index) in groupStats"
															:key="group.code || 'empty-' + index"
															type="button"
															class="ca-chip"
															:class="{
																'ca-chip--active': isActiveGroup(group),
																'ca-chip--static': !group.code,
															}"
															:title="chipTitle(group)"
															@click.prevent.stop="toggleGroup(group)"
														>
															<span
																class="ca-chip__dot"
																:style="{ backgroundColor: groupColor(index) }"
															></span>
															{{ group.title }}
														</button>
													</div>
												</template>
												<div v-else class="ca-empty">{{ $t('customs.no_data') }}</div>
											</div>

											<div class="ca-card">
												<div class="ca-card__title">{{ $t('customs.top_countries') }}</div>
												<div class="ca-card__hint mb-[10px]">
													{{ $t('customs.top_countries_hint') }}
												</div>

												<template v-if="hasCountries">
													<div class="ca-legend">
														<span class="ca-legend__item">
															<span class="ca-legend__dot ca-legend__dot--import"></span>
															{{ $t('customs.import') }}
														</span>
														<span class="ca-legend__item">
															<span class="ca-legend__dot ca-legend__dot--export"></span>
															{{ $t('customs.export') }}
														</span>
													</div>

													<VueApexCharts
														type="bar"
														:height="countryChartHeight"
														:options="countryChartOptions"
														:series="countrySeries"
													></VueApexCharts>

													<div
														v-if="allCountryStats.length > countryStats.length"
														class="ca-note mt-[6px]"
													>
														{{
															$t('customs.top_shown', {
																shown: countryStats.length,
																total: allCountryStats.length,
															})
														}}
													</div>
												</template>
												<div v-else class="ca-empty">{{ $t('customs.no_data') }}</div>
											</div>

											<div class="ca-card">
												<div class="ca-card__title">{{ $t('customs.customs_posts') }}</div>
												<div class="ca-card__hint mb-[10px]">
													{{ $t('customs.customs_posts_hint') }}
												</div>

												<template v-if="hasStates">
													<VueApexCharts
														type="bar"
														:height="stateChartHeight"
														:options="stateChartOptions"
														:series="stateSeries"
													></VueApexCharts>

													<div
														v-if="allStateStats.length > stateStats.length"
														class="ca-note mt-[6px]"
													>
														{{
															$t('customs.top_shown', {
																shown: stateStats.length,
																total: allStateStats.length,
															})
														}}
													</div>
												</template>
												<div v-else class="ca-empty">{{ $t('customs.no_data') }}</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
/* Пока идёт перезапрос, графики остаются на экране, но приглушены. */
.ca-board--loading {
	opacity: 0.55;
	pointer-events: none;
	transition: opacity 0.2s;
}

.ca-card {
	padding: 20px 24px;
	background: #ffffff;
	border: 1px solid #eef1f6;
	border-radius: 10px;
	box-shadow: 0 8px 30px rgba(210, 232, 254, 0.25);
}

.ca-card__title {
	font-family: 'Geologica', sans-serif;
	font-weight: 600;
	font-size: 16px;
	line-height: 1.35;
	color: #151d48;
}

.ca-card__label {
	font-family: 'Geologica', sans-serif;
	font-size: 14px;
	line-height: 1.35;
	color: #425166;
}

.ca-card__value {
	font-family: 'Geologica', sans-serif;
	font-weight: 700;
	font-size: 32px;
	line-height: 1.3;
	color: #151d48;
	margin: 6px 0 4px;
}

.ca-card__hint {
	font-family: 'Geologica', sans-serif;
	font-size: 12px;
	line-height: 1.4;
	color: #7b8ba5;
}

.ca-empty {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 200px;
	font-family: 'Geologica', sans-serif;
	font-size: 14px;
	color: #7b8ba5;
}

.ca-note {
	font-family: 'Geologica', sans-serif;
	font-size: 12px;
	line-height: 1.45;
	color: #7b8ba5;
}

/* Активный drill-down по коду ТН ВЭД — снимается кликом. */
.ca-pill {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 5px 12px;
	border: 1px solid #d3e7fb;
	border-radius: 30px;
	background: #f5faff;
	font-family: 'Geologica', sans-serif;
	font-size: 12px;
	color: #157fee;
}

.ca-pill__close {
	font-size: 16px;
	line-height: 1;
}

/* Товарных групп в реальных данных десятки: список чипсов скроллится,
   чтобы карточка не растягивалась на весь экран. */
.ca-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 12px;
	max-height: 156px;
	overflow-y: auto;
	padding-right: 4px;
}

.ca-chips::-webkit-scrollbar {
	width: 6px;
}

.ca-chips::-webkit-scrollbar-track {
	background: #f5faff;
	border-radius: 3px;
}

.ca-chips::-webkit-scrollbar-thumb {
	background: #d3e7fb;
	border-radius: 3px;
}

.ca-chip {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 7px 14px;
	border: 1px solid #eef1f6;
	border-radius: 8px;
	background: #ffffff;
	font-family: 'Geologica', sans-serif;
	font-size: 13px;
	line-height: 1.2;
	color: #425166;
	transition: 0.2s;
}

.ca-chip:hover {
	border-color: #d3e7fb;
	background: #f5faff;
}

.ca-chip--active {
	border-color: #157fee;
	background: #157fee;
	color: #ffffff;
}

/* Группа без кода ТН ВЭД — фильтровать по ней нечем. */
.ca-chip--static {
	cursor: default;
}

.ca-chip--static:hover {
	border-color: #eef1f6;
	background: #ffffff;
}

.ca-chip__dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	flex-shrink: 0;
}

.ca-legend {
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
	margin-bottom: 6px;
}

.ca-legend__item {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-family: 'Geologica', sans-serif;
	font-size: 12px;
	color: #425166;
}

.ca-legend__dot {
	width: 10px;
	height: 10px;
	border-radius: 3px;
}

.ca-legend__dot--import {
	background: #157fee;
}

.ca-legend__dot--export {
	background: #e0bb49;
}

/* Подсказки рисует apexcharts вне scoped-разметки, поэтому ::v-deep. */
.customs-analytics ::v-deep .apexcharts-tooltip {
	border: 1px solid #eef1f6;
	border-radius: 8px;
	background: #ffffff;
	box-shadow: 0 8px 30px rgba(210, 232, 254, 0.6);
}

.customs-analytics ::v-deep .ca-tip {
	min-width: 180px;
	padding: 10px 12px;
	font-family: 'Geologica', sans-serif;
	font-size: 12px;
	color: #425166;
}

.customs-analytics ::v-deep .ca-tip__title {
	max-width: 260px;
	margin-bottom: 6px;
	font-weight: 600;
	color: #151d48;
	white-space: normal;
}

.customs-analytics ::v-deep .ca-tip__row {
	display: flex;
	justify-content: space-between;
	gap: 16px;
	padding: 2px 0;
}

.customs-analytics ::v-deep .ca-tip__row b {
	color: #151d48;
	font-weight: 600;
}
</style>
